import { Constructor } from '@angular/cdk/table';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { ISlide } from 'src/app/core/dtos/interfaces/i-slide';
import { compareArrays } from '../utils/array-utils';
import { takeUntil } from 'rxjs/operators';
import { MediaService } from '../services/media.service';
import { deepEqual } from '../utils/deep-equal';

type WithSlidesFormSectionType = {
  _initSlides(slideFormArray: ISlide[]);
  _initSlidesValueChanges(getPreviousSlidesValue: () => ISlide[] | null);
  _buildSlides();
  _buildMaxSlideId();
};

type WithSlidesFormSectionRequirementsBase = {
  form: FormGroup;
  FORM_PATHS: {
    SLIDES: () => string;
  };
  destroy$: Subject<boolean>;
  mediaService: MediaService;
  formBuilder: FormBuilder;
};

export function WithSlidesFormSection<
  TBase extends Constructor<WithSlidesFormSectionRequirementsBase>
>(
  baseClass: TBase
): {
  new (...args: any[]): WithSlidesFormSectionType;
  prototype: WithSlidesFormSectionType;
} & TBase {
  return class extends baseClass {
    constructor(...args: any[]) {
      super(...args);
    }

    _initSlides(slideFormArray: ISlide[]): void {
      let slides = this.form.get(this.FORM_PATHS.SLIDES()) as FormArray<
        FormControl<ISlide>
      >;
      slideFormArray?.forEach((slide, index) => {
        slide.id = index + 1;
        slides.push(new FormControl(slide));
      });
    }

    _initSlidesValueChanges(
      getPreviousSlidesValue: () => ISlide[] | null
    ): void {
      this.form
        .get(this.FORM_PATHS.SLIDES())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue: ISlide[]) => {
            if (compareArrays(newValue, getPreviousSlidesValue())) {
              this.form.get(this.FORM_PATHS.SLIDES()).markAsPristine();
            } else {
              this.form.get(this.FORM_PATHS.SLIDES()).markAsDirty();
              return;
            }
            this.form
              .get(this.FORM_PATHS.SLIDES())
              .value.forEach((slide, index) => {
                if (deepEqual(slide, getPreviousSlidesValue()[index])) {
                  this.form.get(this.FORM_PATHS.SLIDES()).markAsPristine();
                } else {
                  this.form.get(this.FORM_PATHS.SLIDES()).markAsDirty();
                  return;
                }
              });
          },
        });
    }

    _buildSlides(): ISlide[] {
      return this.form.get(this.FORM_PATHS.SLIDES()).value;
    }

    _buildMaxSlideId(): number {
      const slideIds = this.form
        .get(this.FORM_PATHS.SLIDES())
        .value.map((slide) => slide.id);
      return slideIds?.length > 0
        ? slideIds.reduce((a, b) => Math.max(a, b))
        : 0;
    }
  };
}
